/* globals d3 */
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import RangeSliderInput from "common/RangeSliderInput";
import { retrieveRange } from "common/api";
import { dosSynRetrieval, dosVolumeRetrieval } from "../api";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import IntervalChart from "common/graphs/IntervalChart/IntervalChart";
import ActionsContext from "common/ActionsContext";
import { hoursScopeChoices } from "common/constants";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

const ChartContainer = styled.div``;

function getIndexesVol(header) {
  return header
    .trim()
    .split(/\s+/)
    .reduce((acc, value, index) => {
      if (value === "MBYTES") {
        acc[`MBYTES${index}`] = index;
      } else {
        acc[value] = index;
      }
      return acc;
    }, {});
}

const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S");

const getSecondsDuration = (duration) => {
  const [hours, minutes, seconds] = duration.split(":");
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

const createInterval = (date, duration, type, details) => {
  const start = new Date(parseDate(date));
  const end = new Date(start.getTime() + getSecondsDuration(duration) * 1000);

  return {
    iniDate: start,
    finDate: end,
    level: null,
    duration,
    type,
    details,
  };
};

const parseVolume = (response) => {
  const [header, ...rows] = response.trim().split("\n");
  const headerIndex = getIndexesVol(header);
  return rows.map( row => {
    const attrs = row.trim().split(/\s+/);
    const details = {
      ip: attrs[headerIndex["IP-ADDRESS"]],
      megabytes: attrs[headerIndex["MBYTES4"]],
      rate: attrs[headerIndex["RATE-Mbps"]],
      top: attrs[headerIndex["TOP-CONTRIBUTOR"]],
      topmbytes: attrs[headerIndex["MBYTES8"]],
    };
    const time = attrs[headerIndex["TIME"]];
    const duration = attrs[headerIndex["DURATION"]];
    const interval = createInterval(time, duration, 0, details);
    return interval;
  });
};

function getIndexesSyn(header) {
  return header
    .trim()
    .split(/\s+/)
    .reduce((acc, value, index) => {
      if (value === "FAIL-SYN") {
        acc[`FAIL-SYN${index}`] = index;
      } else {
        acc[value] = index;
      }
      return acc;
    }, {});
}

const getType = (side, direction) => {
  if (side == "access") {
    if (direction == "ingress") return 1;
    else return 2;
  } else {
    return null;
  }
};

const parseSyn = (response) => {
  const [header, ...rows] = response.trim().split("\n");
  const headerIndex = getIndexesSyn(header);
  return rows.reduce((acc, row) => {
    const attrs = row.trim().split(/\s+/);
    const details = {
      ip: attrs[headerIndex["IP-ADDRESS"]],
      failsyn: attrs[headerIndex["FAIL-SYN4"]],
      maxrate: attrs[headerIndex["MAX-RATE"]],
      top: attrs[headerIndex["TOP-CONTRIBUTOR"]],
      topfailsyn: attrs[headerIndex["FAIL-SYN8"]],
    };
    const type = getType(
      attrs[headerIndex["SIDE"]],
      attrs[headerIndex["DIRECTION"]]
    );
    if (type !== null) {
      const time = attrs[headerIndex["TIME"]];
      const duration = attrs[headerIndex["DURATION"]];
      const interval = createInterval(time, duration, type, details);
      acc.push(interval);
    }
    return acc;
  }, []);
};

export const useDosOverTimeData = (hours = 24, firstRequest = null) => {
  const [request, setRequest] = useState(firstRequest);
  const [initialRange, setInitialRange] = useState();
  const actions = useContext(ActionsContext);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const doRequest = (hours) => {
    return retrieveRange(hours, mode, bqnId).then(({ from, to }) => {
      setInitialRange({ from, to });
      const promises = [
        dosVolumeRetrieval({ hours, mode, bqnId }),
        dosSynRetrieval({ hours, mode, bqnId }),
      ];
      return Promise.all(promises).then(([volumeResponse, synResponse]) => {
        const dataSetDosOverTime = [
          ...parseVolume(volumeResponse),
          ...parseSyn(synResponse),
        ];
        const dataSetDosOverTimeSorted = dataSetDosOverTime.sort(function (
          a,
          b
        ) {
          if (a.iniDate < b.iniDate) return -1;
          if (a.iniDate > b.iniDate) return 1;
          return 0;
        });
        return { result: dataSetDosOverTimeSorted };
      });
    });
  };

  const doLoad = () => {
    setRequest(doRequest(hours));
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad();
    return actions.recv('do-load', doLoad);
  }, [hours, mode, bqnId]);
  return { request, doLoad, initialRange };
};

const DosOverTimeReport = () => {
  const [hours, setHours] = useState(24);
  const { request, initialRange } = useDosOverTimeData(hours, null);
  const [range, setRange] = useState();
  return (
    <Wrapper>
      <ColumnsGrid rowGap="0">
        <SelectWrapper>
          <SelectInputWithIcon
            label="Date Range"
            name="Date Range"
            icon="date_range"
            selected={hours}
            onChange={({ target }) => setHours(target.value)}
            options={hoursScopeChoices}
          />
        </SelectWrapper>
      </ColumnsGrid>
      <ChartContainer>
        <Request during={request}>
          {({ result }) => {
            return (
              <IntervalChart range={range || initialRange} data={result} />
            );
          }}
        </Request>
      </ChartContainer>
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default DosOverTimeReport;
